import { useMutation } from '@apollo/client';
import { Pencil } from 'lucide-react';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArticleSources } from '../../components/Dialogs/ArticleSources';
import { isSafeHttpUrl } from '../../components/Dialogs/ArticleSources/helpers/isSafeHttpUrl';
import {
  computeDuplicateErrors,
  findDuplicateIndex,
  normalizeUrlForCompare,
} from '../../components/Dialogs/ArticleSources/helpers/normalizeUrlForComparison';
import { sanitizeInput } from '../../components/Dialogs/ArticleSources/helpers/sanitizeInput';
import { TextAnalysisResponse } from '../../components/Dialogs/TextAnalysisResponse';
import { EnlargeableImage } from '../../components/EnlargeableImage';
import ButtonComponent from '../../components/UI/Button/Button.component';
import { DRAFT } from '../../constants/draft.constants';
import { charCounter } from '../../constants/post.constants';
import { POST_REPLY_DRAFT } from '../../constants/postReplyDraft.constants';
import { PRIVATE_URIS } from '../../constants/privateURIs.constants';
import { GET_DRAFT_BY_ID } from '../../graphql/resolvers/draftQueries/getDraftById.gql';
import { DELETE_REPLY_TO_POST_DRAFT_MUTATION } from '../../graphql/resolvers/replyToPost/mutations/deleteReplyToPostDraft.gql';
import { PUBLISH_POST_REPLY } from '../../graphql/resolvers/replyToPost/mutations/publishPostReply.gql';
import { CREATE_ANALYSIS_MUTATION } from '../../graphql/resolvers/storyAnalysis/createAnalysis.gql';
import { DELETE_STORY_ANALYSIS_MUTATION } from '../../graphql/resolvers/storyAnalysis/mutations/deleteStoryAnalysis.gql';
import { formatDate } from '../../helpers/dateConverters';
import { replyPublishDisabledReason as getReplyPublishDisabledReason } from '../../helpers/publishGateReason';
import { sanitizeDraft } from '../../helpers/removeTypenameFromPayload';
import { renderLinkedText } from '../../helpers/renderLinkedText';
import { shouldReuseStoredReplyAnalysis } from '../../helpers/replyAnalysisGate';
import { extractTextFromBlocks } from '../../helpers/textBlockExtractor';
import { triggerAnalysis } from '../../helpers/triggerAnalysis';
import { useContentDeletion } from '../../hooks/useContentDeletion';
import { usePublishConstraints } from '../../hooks/usePublishConstraints';
import { usePublishPostReplyOptimistic } from '../../hooks/usePublishPostReplyOptimistic';
import { useReplyDrafts } from '../../hooks/useReplyDrafts';
import { useStoryAnalyzer } from '../../hooks/useStoryAnalyzer';
import { AnalysisResponse } from '../../interfaces/analysisResponse';
import { FeedPost } from '../../interfaces/feed.interface';
import { GlobalItemsState } from '../../interfaces/global.interface';
import {
  PostDraftInterface,
  PostReplyDraftInterfaceState,
  PostReplyDraftsInterfaceState,
} from '../../interfaces/post.interface';
import { SentimentResponse } from '../../interfaces/sentimentResponse';
import type { TextBlock } from '../../interfaces/textEditor.interface';
import type { UserState } from '../../interfaces/user.interface';
import { globalItemsSlice } from '../../store/globalItems.store';
import { postReplyDraftSlice } from '../../store/postReplyDraft.store';
import { postReplyDraftsSlice } from '../../store/postReplyDrafts.store';
import { userSlice } from '../../store/user.store';
import { FeedEntryType } from '../../types/FeedEntryType.type';
import { DeleteDraftConfirm } from '../PostDraft/DeleteDraftConfirm';
import { DraftCharCounter } from '../PostDraft/DraftCharCounter';
import DraftCategories from './DraftCategories';
import '../../styles/post.scss';
import '../../styles/utilities.scss';
import './post-reply-draft.scss';

/**
 * TRIL-620: extracted from the component body to MODULE SCOPE so the block rendering —
 * including the enlarge affordance — can be rendered by a test without standing up
 * the reply-draft preview's Redux/Apollo/Router/Auth/Websocket providers.
 *
 * It was already a pure function of `block` (no closure state), so this is a move, not a
 * rewrite. Without it the wiring is untestable in practice and CI cannot tell whether this
 * surface renders an enlargeable image at all — which is exactly what review found: all
 * four web call sites could be reverted with the suite still fully green.
 */
export const renderReplyDraftBlock = (block: TextBlock) => {
  switch (block.type) {
    case 'Heading': {
      const level = block.data?.level ?? 1;
      const HeadingTag = `h${level}` as React.ElementType;
      return (
        <HeadingTag className={'POST__HEADER'}>{block.data?.header}</HeadingTag>
      );
    }
    case 'Paragraph':
      return <p>{renderLinkedText(block.data?.paragraph)}</p>;
    case 'List':
      return block.data?.style === 'ordered' ? (
        <ol style={{ paddingLeft: '2rem', listStyleType: 'decimal' }}>
          {block.data?.items?.map((item: any, itemIndex: number) => (
            <li key={itemIndex - item.id}>{item.text}</li>
          ))}
        </ol>
      ) : (
        <ul style={{ paddingLeft: '2rem', listStyleType: 'disc' }}>
          {block.data?.items?.map((item: any, itemIndex: number) => (
            <li key={itemIndex - item.id}>{item.text}</li>
          ))}
        </ul>
      );
    case 'Image':
      return (
        <EnlargeableImage
          src={block.data?.file?.url as string}
          caption={block.data?.caption}
          fallbackAlt="Draft image"
        />
      );
    case 'Code':
      return (
        <pre
          style={{
            backgroundColor: '#f5f5f5',
            padding: '1rem',
            borderRadius: '4px',
            overflowX: 'auto',
          }}
        >
          <code>{block.data?.code}</code>
        </pre>
      );
    case 'Quote':
      return (
        <div>
          <p>{renderLinkedText(block.data?.paragraph)}</p>
          {block.data?.caption && <p>— {block.data.caption}</p>}
        </div>
      );
    default:
      return null;
  }
};

const PostReplyDraftComponent = ({
  userId,
  username,
  parentPost,
  onEditDraft,
}: {
  userId: string;
  username: string;
  parentPost: FeedPost;
  onEditDraft?: () => void;
}): React.ReactElement => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const params = useParams();
  const { refetchDrafts, removeDraft } = useReplyDrafts(userId);
  const {
    minReplyLength,
    error: constraintsError,
    refetch: refetchConstraints,
  } = usePublishConstraints();
  const { postReplyDraftId } = params;
  const { postReplyDrafts } = useSelector(
    (state: PostReplyDraftsInterfaceState) => state.postReplyDraftsStore,
  );
  const { postReplyDraft, reAnalyzeReply } = useSelector(
    (state: PostReplyDraftInterfaceState) => state.postReplyDraftStore,
  );
  const { user } = useSelector((state: UserState) => state.userStore);
  // Race-safe optimistic reply count bump for the replier — see
  // usePublishPostReplyOptimistic for the full invariant, and
  // hooks/__tests__/usePublishPostReplyOptimistic.test.tsx for coverage
  // of the mutation/websocket race orderings.
  const { snapshotBeforePublish, applyOptimisticBump } =
    usePublishPostReplyOptimistic();
  const { openTransitionAlert } = useSelector(
    (state: GlobalItemsState) => state.globalItemsStore,
  );
  const [isDeleting, setIsDeleting] = React.useState(false);
  // TRIL-571 — same guard as the post-draft surface: deletion cascades to S3 content,
  // every version object and the stored analysis, with no undo.
  const [confirmDeleteOpen, setConfirmDeleteOpen] = React.useState(false);
  const [openBackdrop, setOpenBackdrop] = React.useState(false);
  const {
    DELETE_POST_REPLY_DRAFT,
    UPDATE_POST_REPLY_DRAFTS_STORE_ACTION,
    REFETCH_POST_REPLY_DRAFTS,
  } = postReplyDraftsSlice.actions;
  const { SET_OPEN_TRANSITION_ALERT } = globalItemsSlice.actions;
  const {
    PUBLISH_POST_REPLY_INIT,
    PUBLISH_POST_REPLY_SUCCESS,
    RESET_POST_REPLY_DRAFT_STATE,
    SET_RE_ANALYZE_ACTION,
  } = postReplyDraftSlice.actions;
  const { UPDATE_USER } = userSlice.actions;
  const { deleteAllDraftContent, isDeleting: isDeletingContent } =
    useContentDeletion();
  const [allPostReplyDrafts, setAllPostReplyDrafts] = React.useState<
    PostDraftInterface[]
  >([]);
  const [postReplyDraftsArray, setAllPostReplyDraftsArray] = React.useState<
    PostDraftInterface[]
  >([]);
  // AI
  const [storyText, setStoryText] = React.useState('');
  const [showAnalysis, setShowAnalysis] = React.useState(false);
  const {
    analysisJobId,
    isAnalyzingReply,
    testConnection,
    getTestStories,
    getUserReplyStats,
    reanalyzeWithReplies,
    reanalysisResult,
    error: useStoryAnalyzerError,
  } = useStoryAnalyzer();
  const [normalizedArray, setNormalizedArray] =
    React.useState<PostDraftInterface[]>();
  const [currentDraft, setCurrentDraft] = React.useState<PostDraftInterface>();
  const [connectionStatus, setConnectionStatus] = React.useState('checking');
  const [draftTextAnalysis, setDraftTextAnalysis] = React.useState<
    AnalysisResponse | undefined
  >(undefined);
  const [draftTextSentiment, setDraftTextSentiment] = React.useState<
    SentimentResponse | undefined
  >(undefined);
  const [showAnalysisModal, setShowAnalysisModal] = React.useState(false);
  const [stringifiedStoryForAnalysis, setStringifiedStoryForAnalysis] =
    React.useState<string | undefined>(undefined);
  const [reAnalyzeButtonLabel, setReAnalyzeButtonLabel] = React.useState(false);
  const [showLastAnalysis, setShowLastAnalysis] = React.useState(false);
  const [showSourcesModal, setShowSourcesModal] = React.useState(false);
  const [categoriesExist, setCategoriesExist] = React.useState(false);
  const [articleSourcesArray, setArticleSourcesArray] = React.useState<
    string[]
  >([]);
  const [errors, setErrors] = React.useState<Array<string | null>>(() =>
    Array(DRAFT.ADD_SOURCES_DIALOG.MAX_SOURCES).fill(null),
  );
  const [categoriesFromParentPost, setCategoriesFromParentPost] =
    React.useState<FeedEntryType[]>([]);

  const currentDraftRef = React.useRef(currentDraft);
  React.useEffect(() => {
    currentDraftRef.current = currentDraft;
  }, [currentDraft]);

  React.useEffect(() => {
    const checkConnection = async () => {
      const isConnected = await testConnection();
      setConnectionStatus(isConnected ? 'connected' : 'disconnected');
    };
    checkConnection();
  }, [testConnection]);

  React.useEffect(() => {
    extractTextFromBlocks(currentDraft?.textBlocks as TextBlock[]);
    setStringifiedStoryForAnalysis(
      extractTextFromBlocks(currentDraft?.textBlocks as TextBlock[]),
    );
  }, [currentDraft?.textBlocks]);

  React.useEffect(() => {
    if (
      (!reAnalyzeReply && draftTextAnalysis?.plugin_results) ||
      postReplyDraft?.hasStoryAnalysis
    ) {
      setShowLastAnalysis(true);
    } else if (reAnalyzeReply && draftTextAnalysis?.plugin_results) {
      setShowLastAnalysis(false);
    }
  }, [reAnalyzeReply, draftTextAnalysis]);

  const handleAnalyzeReply = async () => {
    if (
      shouldReuseStoredReplyAnalysis(
        reAnalyzeReply,
        showLastAnalysis,
        postReplyDraft?.hasStoryAnalysis,
      )
    ) {
      setShowAnalysisModal(true);
      return;
    }

    if (draftTextAnalysis && draftTextSentiment && !reAnalyzeReply) {
      setShowAnalysisModal(true);
      return;
    }

    const replyText = stringifiedStoryForAnalysis as string;

    try {
      setOpenBackdrop(true);
      // TRIL-182: replies use the same full story analyzer as posts so the
      // result matches the storyAnalyzer schema + UI. Previously this used the
      // reply-specific /api/replies/analyze, whose lighter shape (and an
      // un-unwrapped wrapper) left storyAnalyzer empty.
      const { analysis, sentiment } = await triggerAnalysis({
        storyText: replyText,
        articleSources: articleSourcesArray,
        verifySources: !!articleSourcesArray.length,
      });

      setDraftTextAnalysis(analysis as any);
      setDraftTextSentiment(sentiment as any);

      // TRIL-542: clears the "content changed, re-analysis allowed" flag now that
      // a fresh analysis has landed. Must stay inside `try`, NOT `finally` — on a
      // failed analysis the flag has to survive so the user can retry. Clearing it
      // on failure would re-lock Analyze and reproduce the bug this fixes.
      if (reAnalyzeReply) {
        setShowLastAnalysis(false);
        dispatch(SET_RE_ANALYZE_ACTION(false));
      }
    } catch (err) {
      setOpenBackdrop(false);
      console.error('Analysis failed:', err);
    } finally {
      setOpenBackdrop(false);
      setShowAnalysisModal(true);
    }
  };

  const [storyHasAnalysis, setStoryHasAnalysis] = React.useState(
    postReplyDraft?.hasStoryAnalysis,
  );

  React.useEffect(() => {
    if (
      postReplyDraft?.storyAnalysis?.length &&
      postReplyDraft?.storyAnalysis?.length > 0
    ) {
      const { sentiment } = postReplyDraft?.storyAnalysis[0] as any;
      setDraftTextSentiment(sentiment);
    }
  }, [postReplyDraft?.storyAnalysis]);

  // Mirror the current draft's analysis state in BOTH directions, keyed on the
  // draft id + its hasStoryAnalysis. Previously this only ever set `true`, so
  // switching from an analyzed reply draft to an unanalyzed one in the same
  // component instance left Publish wrongly enabled (TRIL-183 review). The
  // in-session createAnalysis sets it true directly and does not change these
  // Redux deps, so this effect won't clobber that.
  React.useEffect(() => {
    setStoryHasAnalysis(!!postReplyDraft?.hasStoryAnalysis);
  }, [postReplyDraft?.id, postReplyDraft?.hasStoryAnalysis]);

  const loadTestStory = async () => {
    const testStories = await getTestStories();
    if (testStories.length > 0) {
      setStoryText(testStories[0].text);
    }
  };

  const handleEdit = () => console.log('handleEdit');

  function normalizeToArray<T>(data: T[] | Record<any, any>): T[] {
    if (Array.isArray(data)) {
      return data; // Already an array, return as is
    } else if (typeof data === 'object') {
      return [data]; // Wrap the nested object in an array
    }
    throw new Error(
      'Invalid input type: Expected an array of objects or an object wrapped in another object.',
    );
  }

  React.useEffect(() => {
    if (postReplyDrafts) {
      setAllPostReplyDrafts(postReplyDrafts);
      const draftsArr = normalizeToArray(postReplyDrafts);
      setAllPostReplyDraftsArray(draftsArr);
    }
  }, [postReplyDrafts]);

  const PUBLISH_POST_REPLY_VARIABLES = {
    userId: '',
    textBlocks: [],
    status: 'published',
    categories: [],
    article: true,
    id: '',
    parentPostId: '',
  };

  const handleClickClose = async () => {
    setShowAnalysisModal(false);
  };

  const STORY_ANALYSIS_VARIABLES = {
    userId: userId,
    postReplyDraftId,
    postReplyAnalysis: true,
    published: false,
    analysis: [],
    sources: articleSourcesArray,
  };

  const [
    createAnalysis,
    {
      data: createAnalysisData,
      loading: createAnalysisLoading,
      error: createAnalysisError,
    },
  ] = useMutation(CREATE_ANALYSIS_MUTATION, {
    variables: {
      input: STORY_ANALYSIS_VARIABLES,
    },
    refetchQueries: [
      { query: GET_DRAFT_BY_ID, variables: { id: postReplyDraftId } },
    ],
    awaitRefetchQueries: true,
    onCompleted: ({ createAnalysis }) => {
      console.log('onCompleted createAnalysis', createAnalysis);
    },
    onError: (ex) => {
      console.error('CREATE_ANALYSIS_MUTATION error =>', ex);
    },
  });

  const [deleteStoryAnalysis] = useMutation(DELETE_STORY_ANALYSIS_MUTATION, {
    variables: {
      id: postReplyDraft?.id,
      userId: userId,
    },
    onCompleted: ({ deleteDraftAnalysis }) => {},
    onError: (ex) => {
      console.error('DELETE_POST_REPLY_DRAFT_ANALYSIS_MUTATION error =>', ex);
    },
  });

  const [deleteReplyToPostDraft] = useMutation(
    DELETE_REPLY_TO_POST_DRAFT_MUTATION,
    {
      variables: {
        id: postReplyDraft?.id,
        username: username,
        userId: userId,
        parentPostId: postReplyDraft.parentPostId as string,
      },
      onCompleted: ({ deleteReplyToPostDraft }) => {
        const updatedDrafts = postReplyDrafts.length
          ? postReplyDrafts.filter((d) => d.id !== postReplyDraft?.id)
          : [];
        dispatch(UPDATE_POST_REPLY_DRAFTS_STORE_ACTION(updatedDrafts));
        const updatedUserObject = {
          ...user,
          postReplyDrafts: updatedDrafts,
        };
        dispatch(UPDATE_USER(updatedUserObject));
        dispatch(RESET_POST_REPLY_DRAFT_STATE(true));
      },
      onError: (ex) => {
        console.error('DELETE_REPLY_TO_POST_DRAFT_MUTATION error =>', ex);
      },
    },
  );

  const [publishPostReply, { data, loading, error }] = useMutation(
    PUBLISH_POST_REPLY,
    {
      variables: {
        input: {
          ...PUBLISH_POST_REPLY_VARIABLES,
        },
      },
      onCompleted: ({ publishPostReply }) => {
        const parentPostId =
          publishPostReply?.parentPostId ?? postReplyDraft?.parentPostId;
        if (parentPostId) applyOptimisticBump(parentPostId);
      },
      onError: (ex) => {
        console.error('PUBLISH_POST_REPLY error =>', ex);
      },
    },
  );

  React.useEffect(() => {
    if (parentPost?.feedMetadata?.categories?.length) {
      const parentCategories = parentPost?.feedMetadata?.categories;
      setCategoriesExist(true);
      setCategoriesFromParentPost(parentCategories);
    } else {
      setCategoriesExist(false);
    }
  }, [parentPost]);

  const renderBlock = renderReplyDraftBlock;

  const handleDeleteDraft = async () => {
    setConfirmDeleteOpen(false);
    setIsDeleting(true);
    try {
      // const apiUrl = process.env.REACT_APP_API_DRAFT_CONTENT_BLOCKS || 'https://hgsimwkg2f.execute-api.us-east-2.amazonaws.com';
      // const stage = process.env.REACT_APP_STAGE || 'dev';
      // const key = `${user.id}/${draft?.id}/`
      // const contentToDeleteUrl = `${apiUrl}/${stage}/delete-draft-content-blocks/${key}`;

      await deleteReplyToPostDraft({
        variables: {
          userId: userId, // used to get the user in the database
          id: (postReplyDraft as PostDraftInterface).id,
          username: username,
          parentPostId: postReplyDraft.parentPostId as string,
        },
      });

      const updatedDrafts = postReplyDrafts.length
        ? postReplyDrafts.filter((d) => d.id !== postReplyDraft?.id)
        : [];
      dispatch(UPDATE_POST_REPLY_DRAFTS_STORE_ACTION(updatedDrafts));
      const updatedUserObject = {
        ...user,
        postReplyDrafts: updatedDrafts,
      };

      dispatch(UPDATE_USER(updatedUserObject));
      dispatch(RESET_POST_REPLY_DRAFT_STATE(true));
      dispatch(REFETCH_POST_REPLY_DRAFTS(true));

      const results = await deleteAllDraftContent(
        postReplyDraft?.textBlocks as TextBlock[],
        user.id,
        (postReplyDraft as PostDraftInterface).id,
      );

      navigate(PRIVATE_URIS.DRAFTS.replace(':username', username));
    } catch (e) {
      console.error(e);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleAcceptAnalysis = async () => {
    const CREATE_STORY_ANALYSIS_VARIABLES = {
      published: false,
      analysis: [
        {
          version: 0,
          storyAnalyzer: draftTextAnalysis,
          sentiment: draftTextSentiment,
          sources: articleSourcesArray,
        },
      ],
      userId: userId,
      sources: articleSourcesArray,
      postReplyAnalysis: true,
      draftId: postReplyDraftId,
      postReplyDraftId,
    };

    return await createAnalysis({
      variables: {
        input: {
          ...CREATE_STORY_ANALYSIS_VARIABLES,
        },
      },
    })
      .then((res) => {
        setStoryHasAnalysis(true);
        const updateDraftInDrafts = postReplyDrafts.find(
          (replyDraft) => replyDraft.id === res.data.createAnalysis.draftId,
        );
        const otherDraftsInDrafts = postReplyDrafts.filter(
          (replyDraft) => replyDraft.id !== res.data.createAnalysis.draftId,
        );
        const updatedDraftInDrafts = {
          ...updateDraftInDrafts,
          hasStoryAnalysis: true,
          sources: articleSourcesArray,
          storyAnalysis: res.data.createAnalysis.analysis,
        };
        const updatedPayload = [...otherDraftsInDrafts, updatedDraftInDrafts];
        dispatch(UPDATE_POST_REPLY_DRAFTS_STORE_ACTION(updatedPayload));
      })
      .catch((e: any) => console.error(e));
  };

  React.useEffect(() => {
    if (postReplyDrafts?.length) {
      const normalizedToArray = normalizeToArray(postReplyDrafts);
      setNormalizedArray(normalizedToArray as PostDraftInterface[]);
    }
  }, [postReplyDrafts, normalizeToArray]);

  React.useEffect(() => {
    if (normalizedArray?.length) {
      const normalizedPost = normalizedArray?.find(
        (d) => d?.id === (postReplyDraft as PostDraftInterface)?.id,
      );
      if (normalizedPost !== undefined) {
        setCurrentDraft(normalizedPost);
      }
    }
  }, [normalizedArray, postReplyDraft]);

  const handleChange = (index: number, value: string) => {
    // sanitize on the way in (keeps state clean)
    const sanitized = sanitizeInput(value);
    setArticleSourcesArray((prev) => {
      const next = [...prev];
      next[index] = sanitized;
      return next;
    });

    // As user types, clear “duplicate” messages and recompute duplicates gently
    setErrors((prev) =>
      computeDuplicateErrors(
        // use the soon-to-be state (sanitized inserted)
        articleSourcesArray.map((v, i) => (i === index ? sanitized : v)),
        // keep any non-duplicate errors they already had
        prev,
      ),
    );
  };

  const handleBlur = (index: number, e: any) => {
    const v = e.target.value.trim();
    // empty is allowed while typing; clear error
    if (!v) {
      // return setErrors(e => (e[index] = DRAFT.ADD_SOURCES_DIALOG.ERROR.INVALID_URL, [...e]));
      setErrors((prev) => {
        const next = [...prev];
        // @ts-ignore
        next[index] = null;
        return next;
      });
      return;
    }

    // Must start with http(s) and pass safety validator
    const startsWithHttp = /^https?:\/\//i.test(v);
    if (!startsWithHttp || !isSafeHttpUrl(v)) {
      setErrors((prev) => {
        const next = [...prev];
        next[index] = DRAFT.ADD_SOURCES_DIALOG.ERROR.INVALID_URL;
        return next;
      });
      return;
    }

    // Duplicate check
    const values = articleSourcesArray.map((s, i) => (i === index ? v : s));
    const dup = findDuplicateIndex(values, index);
    setErrors((prev) => {
      const next = [...prev];
      // @ts-ignore
      next[index] = dup !== null ? `Duplicate of Source #${dup + 1}` : null;
      return next;
    });

    const ok = isSafeHttpUrl(v);
    // setErrors(e => ((e as any)[index] = ok ? null : DRAFT.ADD_SOURCES_DIALOG.ERROR.INVALID_URL, [...e]));
  };

  // Trim & keep non-empty (still raw strings; validation happens on submit)
  const cleanedSources = React.useMemo(
    () => articleSourcesArray.map((s) => s.trim()).filter(Boolean),
    [articleSourcesArray],
  );

  const onSubmitArticleSources = () => {
    const uniques = new Set<string>();
    const safe: string[] = [];
    for (const s of articleSourcesArray) {
      const v = s.trim();
      if (!v) continue;
      if (!isSafeHttpUrl(v)) continue;
      const canon = normalizeUrlForCompare(v);
      if (!canon) continue;
      if (uniques.has(canon)) continue;
      uniques.add(canon);
      safe.push(v); // keep original (or push canon if you prefer)
    }
    //
    // // If you prefer to hard-fail when any invalid is present, swap with Option B below.
    handleAddSources(safe);
  };

  const handleAddSources = (values: string[]) => {
    setArticleSourcesArray(values);
    setShowLastAnalysis(false);
  };

  const handleOpenSourcesModal = async () => {
    setShowSourcesModal(true);
  };

  const handleCloseSourcesModal = async () => {
    setShowSourcesModal(false);
  };
  const onClose = async () => {
    await handleCloseSourcesModal();
  };

  const handleCheckUserReplyStats = React.useCallback(async () => {
    if (!userId) return;
    try {
      setOpenBackdrop(true);

      // check user rate limits (cooldown)
      const userReplyStats = await getUserReplyStats(userId);
      const hasCooldown = userReplyStats?.stats?.has_cooldown;

      if (hasCooldown) {
        // Show UI message to user
        dispatch(SET_OPEN_TRANSITION_ALERT(true));
        return;
      }

      // If no cooldown, proceed
      return await handlePublishPostReplyDraft();
    } catch (error) {
      console.error('Failed to check user reply stats:', error);
    } finally {
      setOpenBackdrop(false);
    }
  }, [userId, getUserReplyStats, dispatch, setOpenBackdrop]);

  // Publishing a draft
  const handlePublishPostReplyDraft = React.useCallback(async () => {
    dispatch(PUBLISH_POST_REPLY_INIT(true));
    const draftToPublish = currentDraftRef.current;

    if (!draftToPublish) {
      console.error('draftToPublish not found or state is stale.');
      return;
    }

    try {
      const { textBlocks, textBlocksVersions, ...restOfDraft } =
        postReplyDraft as PostDraftInterface;
      const sanitizeTextBlocks = () =>
        textBlocks.map((textBlock) => {
          let textBlockObj = {};
          // @ts-ignore
          const { __typename, items, ...restOfTextBlock } = textBlock.data;
          textBlockObj = {
            ...restOfTextBlock,
            data: {
              ...textBlock.data,
              items: [],
            },
          };
          if (items) {
            // @ts-ignore
            items.map((item: any, index: number) => {
              const { __typename, ...restOfItem } = item;
              return (textBlockObj = {
                ...textBlockObj,
                data: {
                  ...textBlock.data,
                  items: restOfItem,
                },
              });
            });
          }
          return textBlockObj;
        });

      const sanitizedTextBlocks = sanitizeTextBlocks();

      // Updated payload for publishPostReply mutation
      const addPostPayload = {
        id: draftToPublish.id, // Pass the postReplyDraft ID - this is crucial!
        parentPostId: draftToPublish.parentPostId as string,
        textBlocks: draftToPublish.textBlocks, // need to first sanitize and remove the __typename property
        status: 'published',
        categories: categoriesFromParentPost,
        userId,
        username,
        article: true,
      };

      const sanitizedAddPostPayload = sanitizeDraft(addPostPayload);

      // Capture the parent's current reply count so the onCompleted
      // callback can compute max(current, preCount + 1) even if the WS
      // broadcast arrives before the Apollo callback fires.
      snapshotBeforePublish(addPostPayload.parentPostId as string);

      await publishPostReply({
        variables: {
          input: {
            ...addPostPayload,
            textBlocks: sanitizedAddPostPayload.textBlocks,
          },
        },
      });

      // Remove draft from unified store immediately (optimistic)
      if (postReplyDraft?.id) {
        removeDraft(postReplyDraft.id);
      }

      // Refetch from server to ensure consistency
      await refetchDrafts();

      // Update legacy stores for backward compatibility
      const updatedDrafts: PostDraftInterface[] = postReplyDrafts.length
        ? postReplyDrafts.filter((d) => d.id !== postReplyDraft?.id)
        : [];
      dispatch(UPDATE_POST_REPLY_DRAFTS_STORE_ACTION(updatedDrafts));
      dispatch(PUBLISH_POST_REPLY_INIT(false));
      dispatch(PUBLISH_POST_REPLY_SUCCESS(true));
      dispatch(UPDATE_USER({ ...user, postReplyDrafts: updatedDrafts }));
      dispatch(RESET_POST_REPLY_DRAFT_STATE(true));

      setStoryText('');
      setShowAnalysis(false);

      const parentPostId = draftToPublish.parentPostId as string;
      if (username && parentPostId) {
        navigate(
          PRIVATE_URIS.POST.replace(':username', username).replace(
            ':postId',
            parentPostId,
          ),
        );
      }
    } catch (err) {
      console.error('Error publishing draftToPublish:', err);
      dispatch(PUBLISH_POST_REPLY_INIT(false));
    }
  }, [
    postReplyDrafts,
    parentPost,
    categoriesFromParentPost,
    normalizedArray,
    currentDraftRef,
    postReplyDraft,
    allPostReplyDrafts,
    user,
    userId,
    username,
    navigate,
  ]);

  const disableAnalyzeButton = isAnalyzingReply
    ? DRAFT.ANALYZING_REPLY
    : shouldReuseStoredReplyAnalysis(
          reAnalyzeReply,
          showLastAnalysis,
          postReplyDraft?.hasStoryAnalysis,
        )
      ? DRAFT.SHOW_LAST_ANALYSIS
      : reAnalyzeReply
        ? 'Re-Analyze Reply'
        : DRAFT.ANALYZE_REPLY;

  // TRIL-407: replies must be long enough to analyze (measured on the same
  // flattened text the analyzer receives; the server enforces it too).
  const replyStoryLength = (stringifiedStoryForAnalysis as string)?.length ?? 0;
  // TRIL-420: floor comes from the server via usePublishConstraints (null while loading).
  const minReply = minReplyLength; // number | null
  const belowMinReplyLength = minReply !== null && replyStoryLength < minReply;
  const replyPublishDisabledReason = getReplyPublishDisabledReason(
    isAnalyzingReply,
    constraintsError,
    minReply,
    replyStoryLength,
    storyHasAnalysis,
  );

  return (
    <>
      <div className="Container">
        <DraftCategories draftId={(postReplyDraft as PostDraftInterface).id} />
        <div>
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              gap: '1rem',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            {onEditDraft && (
              <ButtonComponent
                iconButton
                size="sm"
                variant={'ghost'}
                onClick={onEditDraft}
                className={'BUTTON_EDIT__DRAFT'}
              >
                <Pencil size={18} />
              </ButtonComponent>
            )}

            <ButtonComponent
              size="sm"
              variant={'outline'}
              type={'submit'}
              onClick={handleAnalyzeReply}
              disabled={isAnalyzingReply || belowMinReplyLength}
              className={'BUTTON__ANALYZE__DRAFT'}
            >
              {disableAnalyzeButton}
            </ButtonComponent>

            <ButtonComponent
              size="sm"
              variant={'outline'}
              type={'submit'}
              className={'BUTTON__ADD__SOURCES__DRAFT'}
              onClick={handleOpenSourcesModal}
              disabled={isAnalyzingReply}
            >
              {DRAFT.ADD_SOURCES}
            </ButtonComponent>

            <ButtonComponent
              size="sm"
              variant={'outline'}
              type={'submit'}
              className={'BUTTON__PUBLISH__POST__REPLY__DRAFT'}
              onClick={handleCheckUserReplyStats}
              disabled={isAnalyzingReply || replyPublishDisabledReason !== null}
            >
              {DRAFT.PUBLISH_REPLY}
            </ButtonComponent>

            <ButtonComponent
              size="sm"
              disabled={isDeleting || isDeletingContent}
              variant={'outline'}
              className={'BUTTON__DELETE__POST__REPLY__DRAFT'}
              onClick={() => setConfirmDeleteOpen(true)}
            >
              {isDeleting || isDeletingContent
                ? 'Deleting...'
                : POST_REPLY_DRAFT.DELETE_POST_REPLY_DRAFT}
            </ButtonComponent>

            {error && (
              <div className="p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
                Error: {useStoryAnalyzerError}
              </div>
            )}
          </div>
          {replyPublishDisabledReason && (
            <p className="DRAFT__PUBLISH__HINT">
              {replyPublishDisabledReason}
              {constraintsError && (
                <button
                  type="button"
                  className="DRAFT__PUBLISH__HINT__RETRY"
                  onClick={() => refetchConstraints()}
                >
                  {charCounter.retry}
                </button>
              )}
            </p>
          )}
          <DraftCharCounter length={replyStoryLength} min={minReplyLength} />
          <div className={'DRAFT__DATES'}>
            {postReplyDraft?.createdAt && (
              <>
                <span>
                  Created on: {formatDate(postReplyDraft?.createdAt as Date)}
                </span>
              </>
            )}
            {postReplyDraft?.createdAt &&
              postReplyDraft?.updatedAt &&
              postReplyDraft?.createdAt !== postReplyDraft?.updatedAt && (
                <>
                  <span>
                    &nbsp;- Updated on:{' '}
                    {formatDate(postReplyDraft?.updatedAt as Date)}
                  </span>
                </>
              )}
          </div>
          {postReplyDraft.status === DRAFT.STATUS.UNPUBLISHED && (
            <div className="flex gap-4 PARENT_POST_LINK">
              <span>{POST_REPLY_DRAFT.LINK.POST_YOU_ARE_REPLYING_TO}</span>
              <Link
                className="read-more-button"
                style={{ textDecoration: 'none' }}
                to={`/post/${postReplyDraft.parentPostId as string}`}
              >
                {parentPost?.textBlocks[0].data.header}
              </Link>
            </div>
          )}
          {postReplyDraft?.textBlocks?.map((block, index) => (
            <React.Fragment key={`${block.id}-${index}`}>
              {renderBlock(block)}
            </React.Fragment>
          ))}
          {showAnalysisModal && (
            <TextAnalysisResponse
              jobId={analysisJobId ?? ''}
              draftId={(postReplyDraft as PostDraftInterface).id}
              userId={userId}
              hasStoryAnalysis={storyHasAnalysis as boolean}
              isModalOpen={showAnalysisModal}
              setShowAnalysisModal={setShowAnalysisModal}
              handleClickClose={handleClickClose}
              analysisResult={draftTextAnalysis as any}
              draftTextSentiment={draftTextSentiment as SentimentResponse}
              handleAcceptAnalysis={handleAcceptAnalysis}
              createAnalysisLoading={createAnalysisLoading}
              handleEdit={handleEdit as () => Promise<void>}
              draftStoryAnalyzerPluginResults={
                postReplyDraft?.storyAnalysis !== undefined &&
                postReplyDraft?.storyAnalysis?.length > 0
                  ? postReplyDraft?.storyAnalysis[0]?.storyAnalyzer
                  : null
              }
            />
          )}
          {showSourcesModal && (
            <ArticleSources
              isSourcesModalOpen={showSourcesModal}
              handleCloseSourcesModal={onClose}
              onSubmitArticleSources={onSubmitArticleSources}
              handleInputChange={handleChange}
              articleSourcesArray={articleSourcesArray}
              errors={errors}
              handleBlur={handleBlur}
            />
          )}
        </div>
      </div>
      {openBackdrop && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            zIndex: 1300,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <span className="spinner" role="status" aria-label="loading" />
        </div>
      )}
      <DeleteDraftConfirm
        open={confirmDeleteOpen}
        onCancel={() => setConfirmDeleteOpen(false)}
        onConfirm={handleDeleteDraft}
      />
    </>
  );
};

export default PostReplyDraftComponent;
